import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CobranzaStats } from '../../utils/cobranza-stats';
import { CobranzaHierarchy, FrequencySplit } from '../../utils/cobranza-hierarchy';

/**
 * Card "Vendí / Cobré / Falta cobrar" del dashboard de seguros.
 *
 * Resumen comercial macro derivado de `CobranzaStats` (regla "vender = póliza
 * emitida" + identidad "pagadas + pagadasConError + pendientes ≡ emitidas").
 *
 * Variante:
 *  - `dashboard`  (default): labels en lenguaje del usuario (Vendí/Cobré/Falta).
 *  - `analytics`: labels existentes en el tab Analítica Avanzada
 *                 (Pagadas/Con error notif/Pendientes/Emitidas).
 *
 * No hace llamadas al backend — el caller pasa las stats ya derivadas.
 */
export type SalesBucket = 'sold' | 'collected' | 'collected_error' | 'pending';
export type SalesCollectionVariant = 'dashboard' | 'analytics';

interface BucketLabels {
  headlineTitle: string;
  headlineSummary: (paid: number, sold: number) => string;
  decompositionTitle: string;
  sold: string;        // bucket azul (Emitidas)
  collected: string;   // bucket verde (Pagadas saneadas)
  collectedError: string; // bucket ámbar (Con error notif)
  pending: string;     // bucket gris (Pendientes — sin pago)
  soldFooter: string;
}

const LABELS_BY_VARIANT: Record<SalesCollectionVariant, BucketLabels> = {
  dashboard: {
    headlineTitle: 'Tasa de cobranza',
    headlineSummary: (paid, sold) => `${paid.toLocaleString('es-VE')} cobradas de ${sold.toLocaleString('es-VE')} vendidas`,
    decompositionTitle: 'Descomposición de lo Vendido',
    sold: 'Vendí',
    collected: 'Cobré',
    collectedError: 'Cobré con error notif.',
    pending: 'Falta cobrar',
    soldFooter: 'Total del período'
  },
  analytics: {
    headlineTitle: 'Cobranza efectiva del período',
    headlineSummary: (paid, sold) => `${paid.toLocaleString('es-VE')} pagadas saneadas de ${sold.toLocaleString('es-VE')} emitidas`,
    decompositionTitle: 'Descomposición de Emitidas',
    sold: 'Emitidas',
    collected: 'Pagadas',
    collectedError: 'Con error notif',
    pending: 'Pendientes',
    soldFooter: 'Total del período'
  }
};

@Component({
  selector: 'app-sales-collection-summary',
  templateUrl: './sales-collection-summary.component.html',
  styleUrls: ['./sales-collection-summary.component.css']
})
export class SalesCollectionSummaryComponent implements OnInit {
  @Input() stats: CobranzaStats | null = null;
  @Input() variant: SalesCollectionVariant = 'dashboard';
  @Input() periodLabel: string = '';
  @Input() insurerLabel: string = 'Todas';
  @Input() loading: boolean = false;
  /** Jerarquía opcional: si se pasa, debajo de cada mini-card aparece un
   *  acordeón con "ver por frecuencia" (Anual / Mensual). */
  @Input() hierarchy: CobranzaHierarchy | null = null;
  /** Total de errores activos por resolver (toda la BD, no solo el período).
   *  Si > 0, se renderiza una card slim debajo con composición + CTA. */
  @Input() paymentErrorsActiveTotal: number = 0;
  /** Emite cuando el usuario clickea en cualquier bucket. El padre decide la
   *  navegación (el componente no la conoce — preparado para drill-down de Act 6). */
  @Output() clickBucket = new EventEmitter<SalesBucket>();
  /** Emite cuando el usuario clickea el CTA "Ver y reintentar" del banner ámbar. */
  @Output() goToPaymentErrors = new EventEmitter<void>();

  /** Estado por bucket del acordeón de frecuencia. Persiste en localStorage. */
  expandedBuckets = new Set<SalesBucket>();
  private readonly LS_KEY = 'insurance_cobranza_hierarchy_expanded_v1';

  ngOnInit(): void {
    try {
      const raw = localStorage.getItem(this.LS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) this.expandedBuckets = new Set(arr.filter(x => typeof x === 'string'));
      }
    } catch { /* ignore */ }
  }

  get labels(): BucketLabels {
    return LABELS_BY_VARIANT[this.variant] || LABELS_BY_VARIANT.dashboard;
  }

  isNaNValue(value: number): boolean {
    return typeof value !== 'number' || isNaN(value);
  }

  pct(part: number, total: number): number {
    if (!total || total <= 0) return 0;
    return (part / total) * 100;
  }

  onBucketClick(bucket: SalesBucket): void {
    this.clickBucket.emit(bucket);
  }

  /** Devuelve el split de frecuencia para cada bucket según la jerarquía. */
  getBucketSplit(bucket: SalesBucket): FrequencySplit | null {
    if (!this.hierarchy) return null;
    switch (bucket) {
      case 'sold':             return this.hierarchy.vendi;
      case 'collected':        return this.hierarchy.pagadasSaneadas;
      case 'collected_error':  return this.hierarchy.pagadasConError;
      case 'pending':          return this.hierarchy.soloEmitidas;
    }
  }

  toggleBucketExpansion(bucket: SalesBucket, event: MouseEvent): void {
    event.stopPropagation();
    if (this.expandedBuckets.has(bucket)) this.expandedBuckets.delete(bucket);
    else this.expandedBuckets.add(bucket);
    try {
      localStorage.setItem(this.LS_KEY, JSON.stringify([...this.expandedBuckets]));
    } catch { /* ignore */ }
  }

  isBucketExpanded(bucket: SalesBucket): boolean {
    return this.expandedBuckets.has(bucket);
  }
}
